import * as React from "react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { useElementOverrides } from "@/contexts/ElementOverrideContext";

// Category color mapping — brand teal icons, white card backgrounds
const categoryColors = {
  communication: {
    bg: "bg-primary/10",
    border: "border-border",
    icon: "text-primary",
    hoverBorder: "hover:border-primary"
  },
  media: {
    bg: "bg-primary/10",
    border: "border-border",
    icon: "text-primary",
    hoverBorder: "hover:border-primary"
  },
  utility: {
    bg: "bg-muted",
    border: "border-border",
    icon: "text-primary",
    hoverBorder: "hover:border-primary"
  },
  help: {
    bg: "bg-accent/10",
    border: "border-border",
    icon: "text-accent",
    hoverBorder: "hover:border-accent"
  }
};

type CategoryType = keyof typeof categoryColors;

interface ActionCardProps {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  category?: CategoryType;
  isFocused?: boolean;
  focusColor?: string;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  iconFillOnFocus?: boolean;
  focusedIconRender?: (props: {className?: string;style?: React.CSSProperties;}) => React.ReactNode;
  className?: string;
  designId?: string; // Unique ID for element-based visual editor
  iconColorClass?: string; // Override icon color class (e.g. from navigation color picker)
  borderHex?: string; // Override border color with hex (overrides element overrides + CSS !important)
}

const sizeClasses = {
  small: {
    card: "p-3 gap-1.5",
    icon: "w-6 h-6",
    label: "text-sm",
    // Fixed sizes for small
    iconStyle: undefined as React.CSSProperties | undefined,
    labelStyle: undefined as React.CSSProperties | undefined
  },
  medium: {
    card: "p-4 gap-2",
    icon: "w-8 h-8",
    label: "text-base font-medium",
    // Fixed sizes for medium
    iconStyle: undefined as React.CSSProperties | undefined,
    labelStyle: undefined as React.CSSProperties | undefined
  },
  large: {
    card: "p-4 gap-2",
    icon: "", // Will use inline style for responsive scaling
    label: "font-semibold text-center leading-tight",
    // Responsive scaling: icon scales with both vh and vmin to fill larger cards proportionally
    iconStyle: {
      width: 'clamp(2.5rem, 10vh, 5rem)',
      height: 'clamp(2.5rem, 10vh, 5rem)'
    } as React.CSSProperties,
    labelStyle: {
      fontSize: 'calc(clamp(1em, 2.8vh, 1.5em) * var(--card-label-scale-cap, 1))'
    } as React.CSSProperties
  }
};

const ActionCard = React.forwardRef<HTMLButtonElement, ActionCardProps>(
  (
  {
    icon: Icon,
    label,
    onClick,
    category = "utility",
    isFocused = false,
    focusColor,
    disabled = false,
    size = "medium",
    iconFillOnFocus = true,
    focusedIconRender,
    className,
    designId,
    iconColorClass,
    borderHex
  },
  ref) =>
   {
    // Fallback to 'utility' if category not found in categoryColors
    const colors = categoryColors[category] || categoryColors.utility;
    const sizes = sizeClasses[size];

    // Detect dark mode
    const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

    // Read element overrides from context (applied at render time, no DOM manipulation needed)
    const { getElementStyle } = useElementOverrides();
    const mode = isDarkMode ? "dark" : "light";
    const overrideStyle = designId ? getElementStyle(designId, mode) : undefined;

    // Build override inline styles
    const overrideInlineStyle: React.CSSProperties = {};
    if (overrideStyle?.backgroundColor) overrideInlineStyle.backgroundColor = overrideStyle.backgroundColor;
    if (overrideStyle?.textColor) overrideInlineStyle.color = overrideStyle.textColor;
    // borderHex from nav color picker takes precedence over element overrides
    if (borderHex) {
      overrideInlineStyle.borderColor = borderHex;
    } else if (overrideStyle?.borderColor) {
      overrideInlineStyle.borderColor = overrideStyle.borderColor;
    }
    if (overrideStyle?.borderWidth) {
      overrideInlineStyle.borderWidth = `${overrideStyle.borderWidth}px`;
      overrideInlineStyle.borderStyle = "solid";
    }

    // Focus overlay: unified outer glow for both light and dark modes (matches sidebar / manual mode)
    const focusOverlayStyle: React.CSSProperties | undefined = isFocused ?
    focusColor ?
    {
      boxShadow: `0 0 0 6px ${focusColor}, 0 0 0 10px ${focusColor}33, 0 0 24px ${focusColor}66`
    } :
    {
      boxShadow: `0 0 0 6px hsl(var(--accent)), 0 0 0 10px hsl(var(--accent) / 0.3), 0 0 24px hsl(var(--accent) / 0.4)`,
      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(251, 146, 60, 0.12)',
    } :
    undefined;

    return (
      <motion.button
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        data-design-id={designId}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={overrideInlineStyle}
        className={cn(
          "relative flex flex-col items-center justify-center rounded-2xl",
          "border-2 transition-all duration-200",
          "bg-card shadow-md",
          colors.border,
          colors.hoverBorder,
          sizes.card,
          isFocused && "z-10 scale-[1.03]",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}>

        {/* Background on hover */}
        <div
          className={cn("absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 hover:opacity-100 border-2",


          colors.bg
          )} />


        {/* Icon */}
        <div className={cn("relative z-10", !borderHex && (iconColorClass || colors.icon))} style={borderHex ? { color: borderHex } : overrideStyle?.iconColor ? { color: overrideStyle.iconColor } : undefined}>
          {isFocused && focusedIconRender ?
          focusedIconRender({ className: sizes.icon, style: sizes.iconStyle }) :

          <Icon
            className={sizes.icon}
            style={sizes.iconStyle}
            strokeWidth={2}
            fill={isFocused && iconFillOnFocus && !isDarkMode ? "white" : "none"} />


          }
        </div>

        {/* Label */}
        <span
          dir="auto"
          className={cn(
            "relative z-10 text-card-foreground text-center leading-tight w-full",
            sizes.label,
            isFocused && !isDarkMode && "scan-dark-text"
          )}
          style={{ 
            ...sizes.labelStyle, 
            ...(overrideStyle?.textColor ? { color: overrideStyle.textColor } : {}),
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            hyphens: 'auto',
          }}>

          {label}
        </span>

        {isFocused &&
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none rounded-2xl"
          style={focusOverlayStyle} />

        }
      </motion.button>);

  }
);

ActionCard.displayName = "ActionCard";

export { ActionCard, categoryColors };
export type { ActionCardProps, CategoryType };